import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ExamsTab from '../components/ExamsTab';
import MovementsTab from '../components/MovementsTab';
import TransfersTab from '../components/TransfersTab';
import OutcomeTab from '../components/OutcomeTab';
import NecropsyTab from '../components/NecropsyTab';
import MilestonesPanel from '../components/MilestonesPanel';
import VitalTrendsPanel from '../components/VitalTrendsPanel';
import FeedingTab from '../components/FeedingTab';
import CameraFileInput from '../components/CameraFileInput';
import ProtectedMediaThumbnail from '../components/ProtectedMediaThumbnail';
import { Download, QrCode, MapPin, Trash2, Pencil, History, ChevronDown, ChevronRight } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { openProtectedFile } from '../lib/protectedFiles';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { appTodayDate, buildAppDateTimeIso, createClientRequestId, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';

const CREATE_NEW_VALUE = '__create_new__';
const DEFAULT_APPETITE_OPTIONS = ['Ótimo', 'Bom', 'Regular', 'Reduzido', 'Ausente', 'Anorexia'];
const DEFAULT_BEHAVIOR_OPTIONS = ['Alerta', 'Apático', 'Prostrado', 'Agressivo', 'Hiporreativo', 'Letárgico'];
const DEFAULT_HYDRATION_OPTIONS = ['Adequada', 'Desidratação Leve', 'Desidratação Moderada', 'Desidratação severa', 'Hiperidratado'];
const DEFAULT_PROGNOSIS_OPTIONS = ['Favoravel', 'Reservado', 'Desfavoravel'];
const DEFAULT_ROUTE_OPTIONS = ['Oral', 'IV', 'IM', 'SC'];
const THERAPY_TYPE_OPTIONS = ['Fisioterapia', 'Laserterapia', 'Termoterapia', 'Outras terapias'];
const DAILY_RECORDS_PAGE_SIZE = 20;
const OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD = 'CONFIRMAR';
const OUTCOME_STATUS_KEYWORDS = ['óbito', 'obito', 'eutanásia', 'eutanasia', 'alta', 'soltura', 'transferência', 'transferencia'];

const normalizeOptionText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const mergeCustomOption = (options, currentValue) => {
  if (!currentValue || options.includes(currentValue)) return options;
  return [...options, currentValue];
};

const mergeUniqueOptions = (...optionGroups) => {
  const seen = new Set();
  const merged = [];
  optionGroups.flat().forEach((item) => {
    const value = String(item || '').trim();
    const key = normalizeOptionText(value);
    if (!value || seen.has(key)) return;
    seen.add(key);
    merged.push(value);
  });
  return merged;
};

const FREQUENCY_MODE_OPTIONS = [
  { value: 'interval_hours', label: 'A cada X horas' },
  { value: 'times_per_day', label: 'X vezes ao dia' },
  { value: 'specific_times', label: 'Horários específicos' },
  { value: 'sos', label: 'SOS / sob demanda' }
];

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const canSetProtocolExecutionDateTime = (user) => ['veterinario', 'administrador', 'programador'].includes(user?.role);

const toDateInputValue = (dateValue = new Date()) => {
  return toAppDateInputValue(dateValue);
};

const toTimeInputValue = (dateValue = new Date()) => {
  return toAppTimeInputValue(dateValue);
};

const buildRecordDateTimeIso = (dateValue, timeValue) => {
  return buildAppDateTimeIso(dateValue, timeValue);
};

const parseOptionalDecimal = (value, label) => {
  const normalized = String(value || '').trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} deve ser um número válido`);
  }
  return parsed;
};

const getRecordDateValue = (record) => record?.date || record?.created_at;

const formatRecordDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Sem data';
  return date.toLocaleDateString('pt-BR');
};

const formatRecordTime = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '--:--';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const normalizeConfirmationText = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const DAILY_RECORD_FIELD_LABELS = {
  date: 'Data/hora do acompanhamento',
  physiological_params: 'Parâmetros fisiológicos',
  body_score: 'Escore corporal',
  clinical_evolution: 'Evolução clínica',
  prognosis: 'Prognóstico',
  requested_exam_types: 'Exames solicitados',
  lab_alert_notes: 'Observação para laboratório'
};

const DAILY_RECORD_PARAM_LABELS = {
  temperature: 'Temperatura',
  glucose: 'Glicose',
  weight: 'Peso',
  appetite: 'Apetite',
  behavior: 'Comportamento',
  hydration: 'Hidratação'
};

const formatAuditValue = (value) => {
  if (value === null || value === undefined || value === '') return 'vazio';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'vazio';
  if (typeof value === 'object') {
    const entries = Object.entries(value)
      .filter(([, item]) => item !== null && item !== undefined && item !== '')
      .map(([key, item]) => `${DAILY_RECORD_PARAM_LABELS[key] || key}: ${item}`);
    return entries.length > 0 ? entries.join('; ') : 'vazio';
  }
  if (typeof value === 'string') {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime()) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return parsedDate.toLocaleString('pt-BR');
    }
  }
  return String(value);
};

const getDailyRecordAuditItems = (record, limit = 5) => {
  const entries = (record?.audit_trail || [])
    .filter((entry) => entry.action !== 'daily_records.delete')
    .slice()
    .reverse();

  return entries.flatMap((entry) => (
    Object.entries(entry.changes || {}).map(([field, change]) => ({
      id: `${entry.id}-${field}`,
      fieldLabel: DAILY_RECORD_FIELD_LABELS[field] || field,
      before: change && Object.prototype.hasOwnProperty.call(change, 'before') ? change.before : null,
      after: change && Object.prototype.hasOwnProperty.call(change, 'after') ? change.after : change,
      userName: entry.user_name || entry.user_email || 'Usuário',
      updatedAt: entry.updated_at
    }))
  )).slice(0, limit);
};

const buildProtocolFrequencyLabel = (mode, value, specificTimes, fallback) => {
  if (mode === 'interval_hours' && value) return `${value}/${value}h`;
  if (mode === 'times_per_day' && value) {
    return specificTimes.length > 0 ? `${value}x/dia (${specificTimes.join(', ')})` : `${value}x/dia`;
  }
  if (mode === 'specific_times' && specificTimes.length > 0) return specificTimes.join(', ');
  if (mode === 'sos') return 'SOS';
  return fallback;
};

const normalizeTimeOptions = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const defaultTimeForIndex = (index, total) => {
  const start = 8;
  const interval = Math.max(1, Math.floor(12 / Math.max(1, total - 1)));
  return `${String((start + index * interval) % 24).padStart(2, '0')}:00`;
};

const normalizeTimesPerDayInput = (currentInput, total) => {
  const count = Math.max(1, Math.min(24, Number(total) || 1));
  const currentTimes = normalizeTimeOptions(currentInput);
  return Array.from({ length: count }, (_, index) => currentTimes[index] || defaultTimeForIndex(index, count)).join(', ');
};

const parseProtocolHours = (protocol) => {
  const startHour = parseInt((protocol.start_time || '08:00').split(':')[0], 10) || 8;
  if (['specific_times', 'times_per_day'].includes(protocol.frequency_mode) && protocol.specific_times?.length > 0) {
    return protocol.specific_times
      .map((item) => parseInt(String(item).split(':')[0], 10))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);
  }
  if (protocol.frequency_mode === 'sos' || String(protocol.frequency || '').toLowerCase().includes('sos')) {
    return [];
  }
  if (protocol.frequency_mode === 'interval_hours' && protocol.frequency_value) {
    const interval = Number(protocol.frequency_value);
    return Array.from({ length: Math.max(1, Math.floor(24 / interval)) }, (_, index) => (startHour + index * interval) % 24);
  }
  if (protocol.frequency_mode === 'times_per_day' && protocol.frequency_value) {
    const times = Number(protocol.frequency_value);
    const interval = Math.max(1, Math.floor(24 / times));
    return Array.from({ length: times }, (_, index) => (startHour + index * interval) % 24).sort((a, b) => a - b);
  }
  const intervalMatch = String(protocol.frequency || '').match(/(\d+)\s*\/\s*(\d+)\s*h/i);
  if (intervalMatch) {
    const interval = Number(intervalMatch[1]);
    return Array.from({ length: Math.max(1, Math.floor(24 / interval)) }, (_, index) => (startHour + index * interval) % 24).sort((a, b) => a - b);
  }
  const dailyMatch = String(protocol.frequency || '').match(/(\d+)\s*x\s*\/?\s*dia/i);
  if (dailyMatch) {
    const times = Number(dailyMatch[1]);
    const interval = Math.max(1, Math.floor(24 / times));
    return Array.from({ length: times }, (_, index) => (startHour + index * interval) % 24).sort((a, b) => a - b);
  }
  return [startHour];
};

const getLabRequestBadgeClass = (status) => {
  switch (status) {
    case 'solicitado':
      return 'bg-amber-100 text-amber-800';
    case 'recebido':
    case 'em_andamento':
      return 'bg-sky-100 text-sky-800';
    case 'em_analise':
      return 'bg-violet-100 text-violet-800';
    case 'concluido':
      return 'bg-emerald-100 text-emerald-800';
    case 'atrasado':
      return 'bg-orange-100 text-orange-800';
    case 'cancelado':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export default function AnimalRecord() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const rawReturnTo = searchParams.get('return_to');
  const animalSearchReturnTo = rawReturnTo && rawReturnTo.startsWith('/animals') ? rawReturnTo : '/animals';
  const [loading, setLoading] = useState(true);
  const [animal, setAnimal] = useState(null);
  const [bases, setBases] = useState([]);
  const [dailyRecords, setDailyRecords] = useState([]);
  const [dailyRecordsPage, setDailyRecordsPage] = useState(1);
  const [hasMoreDailyRecords, setHasMoreDailyRecords] = useState(false);
  const [loadingMoreDailyRecords, setLoadingMoreDailyRecords] = useState(false);
  const [medProtocols, setMedProtocols] = useState([]);
  const [physioProtocols, setPhysioProtocols] = useState([]);
  const [medicationCatalog, setMedicationCatalog] = useState([]);
  const [protocolExecutions, setProtocolExecutions] = useState({});
  const [currentRecordTab, setCurrentRecordTab] = useState('initial');
  const [expandedDailyRecordDates, setExpandedDailyRecordDates] = useState({});
  const [expandedDailyRecords, setExpandedDailyRecords] = useState({});
  const [editingDailyRecord, setEditingDailyRecord] = useState(null);
  const [dailyRecordForm, setDailyRecordForm] = useState({
    record_date: '',
    record_time: '',
    temperature: '',
    glucose: '',
    weight: '',
    appetite: '',
    behavior: '',
    hydration: '',
    clinical_evolution: '',
    prognosis: '',
    requested_exam_types: '',
    lab_alert_notes: ''
  });
  const [savingDailyRecord, setSavingDailyRecord] = useState(false);
  const [savingDailyRecordEdit, setSavingDailyRecordEdit] = useState(false);
  const [dailyRecordClientRequestId, setDailyRecordClientRequestId] = useState(() => createClientRequestId('daily-record'));

  const canManageRecentMedia = (mediaItem) => {
    if (['administrador', 'programador'].includes(user?.role)) return true;
    if (user?.role !== 'veterinario') return false;
    if (mediaItem?.uploaded_by !== user?.id) return false;
    const uploadedAt = new Date(mediaItem?.uploaded_at || mediaItem?.timestamp);
    if (Number.isNaN(uploadedAt.getTime())) return false;
    return Date.now() - uploadedAt.getTime() <= 4 * 60 * 60 * 1000;
  };

  const handleOpenMediaFile = async (event, mediaItem) => {
    event.preventDefault();
    try {
      await openProtectedFile(mediaItem.file_url, mediaItem.caption || mediaItem.filename);
    } catch (error) {
      toast.error(error.message || 'Não foi possível abrir o arquivo');
    }
  };
  
  const [temperature, setTemperature] = useState('');
  const [glucose, setGlucose] = useState('');
  const [weight, setWeight] = useState('');
  const [appetite, setAppetite] = useState('');
  const [behavior, setBehavior] = useState('');
  const [hydration, setHydration] = useState('');
  const [clinicalEvolution, setClinicalEvolution] = useState('');
  const [prognosis, setPrognosis] = useState('');
  const [requestedExamTypes, setRequestedExamTypes] = useState('');
  const [labExamRequests, setLabExamRequests] = useState([{ exam_type: '', laboratory_type: 'interno' }]);
  const [labAlertNotes, setLabAlertNotes] = useState('');
  const [enableLabAlert, setEnableLabAlert] = useState(false);
  const [dailyMediaFiles, setDailyMediaFiles] = useState([]);
  const [recordDate, setRecordDate] = useState(toDateInputValue());
  const [recordTime, setRecordTime] = useState(toTimeInputValue());

  const [showMedForm, setShowMedForm] = useState(false);
  const [editingMedicationProtocol, setEditingMedicationProtocol] = useState(null);
  const [treatmentProtocolType, setTreatmentProtocolType] = useState('medication');
  const [selectedMedicationCatalogId, setSelectedMedicationCatalogId] = useState('');
  const [medicationName, setMedicationName] = useState('');
  const [therapyType, setTherapyType] = useState('Fisioterapia');
  const [therapyDuration, setTherapyDuration] = useState('');
  const [presentation, setPresentation] = useState('');
  const [dosage, setDosage] = useState('');
  const [route, setRoute] = useState('');
  const [frequency, setFrequency] = useState('');
  const [frequencyMode, setFrequencyMode] = useState('interval_hours');
  const [frequencyValue, setFrequencyValue] = useState('8');
  const [specificTimesInput, setSpecificTimesInput] = useState('');
  const [protocolStartDate, setProtocolStartDate] = useState(appTodayDate());
  const [protocolEndDate, setProtocolEndDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [durationDays, setDurationDays] = useState('');
  const [protocolEditReason, setProtocolEditReason] = useState('');
  const [medProtocolClientRequestId, setMedProtocolClientRequestId] = useState(() => createClientRequestId('med-protocol'));
  const [showExecutionDialog, setShowExecutionDialog] = useState(false);
  const [executionProtocol, setExecutionProtocol] = useState(null);
  const [executionScheduledHour, setExecutionScheduledHour] = useState('');
  const [executionStatus, setExecutionStatus] = useState('realizado');
  const [executionNotes, setExecutionNotes] = useState('');
  const [executionDate, setExecutionDate] = useState('');
  const [executionTime, setExecutionTime] = useState('');
  const [executionDateTimeJustification, setExecutionDateTimeJustification] = useState('');
  const [executionMediaFile, setExecutionMediaFile] = useState(null);
  const [executionClientRequestId, setExecutionClientRequestId] = useState(() => createClientRequestId('protocol-execution'));
  const [savingExecution, setSavingExecution] = useState(false);
  const [savingMedicationProtocol, setSavingMedicationProtocol] = useState(false);
  const [editingExecution, setEditingExecution] = useState(null);
  const [uploadingExecutionId, setUploadingExecutionId] = useState(null);
  const [downloadingDetailedReport, setDownloadingDetailedReport] = useState(false);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const [downloadingEnclosureLabel, setDownloadingEnclosureLabel] = useState(false);
  const canDeleteAnimalRecords = hasPermission(user, 'records.delete');
  const canViewDailyHistory = hasPermission(user, 'daily_records.view');
  const canCreateDailyHistory = hasPermission(user, 'daily_records.create');
  const canEditDailyHistory = hasPermission(user, 'daily_records.edit');
  const canDeleteDailyHistory = hasPermission(user, 'daily_records.delete');
  const canAttachDailyHistory = hasPermission(user, 'daily_records.attach');
  const canManageTreatmentProtocols = hasPermission(user, 'animals.edit_clinical');
  const canDeleteAnimal = hasPermission(user, 'animals.delete');
  const canPermanentlyDeleteProtocols = user?.role === 'administrador';
  const animalHasOutcome = Boolean(
    animal?.outcome_date ||
    animal?.outcome ||
    animal?.is_active === false ||
    OUTCOME_STATUS_KEYWORDS.some((keyword) => String(animal?.status || '').toLowerCase().includes(keyword))
  );

  const [creatingAppetite, setCreatingAppetite] = useState(false);
  const [creatingBehavior, setCreatingBehavior] = useState(false);
  const [creatingHydration, setCreatingHydration] = useState(false);
  const [creatingPrognosis, setCreatingPrognosis] = useState(false);
  const [creatingPresentation, setCreatingPresentation] = useState(false);
  const [creatingRoute, setCreatingRoute] = useState(false);

  const appetiteOptions = mergeCustomOption(DEFAULT_APPETITE_OPTIONS, appetite);
  const behaviorOptions = mergeCustomOption(DEFAULT_BEHAVIOR_OPTIONS, behavior);
  const hydrationOptions = mergeCustomOption(DEFAULT_HYDRATION_OPTIONS, hydration);
  const prognosisOptions = mergeCustomOption(DEFAULT_PROGNOSIS_OPTIONS, prognosis);
  const selectedMedicationCatalog = useMemo(
    () => medicationCatalog.find((item) => item.id === selectedMedicationCatalogId) || null,
    [medicationCatalog, selectedMedicationCatalogId]
  );
  const medicationSuggestions = useMemo(() => {
    const search = normalizeOptionText(medicationName);
    return medicationCatalog
      .filter((item) => !search || normalizeOptionText(item.active_ingredient).includes(search))
      .slice(0, 6);
  }, [medicationCatalog, medicationName]);
  const medicationNameExistsInCatalog = useMemo(
    () => medicationCatalog.some((item) => normalizeOptionText(item.active_ingredient) === normalizeOptionText(medicationName)),
    [medicationCatalog, medicationName]
  );
  const presentationOptions = mergeUniqueOptions(selectedMedicationCatalog?.presentations || [], presentation);
  const dosageOptions = mergeUniqueOptions(selectedMedicationCatalog?.suggested_doses || [], dosage);
  const routeOptions = mergeUniqueOptions(DEFAULT_ROUTE_OPTIONS, selectedMedicationCatalog?.routes || [], route);

  useEffect(() => {
    fetchAnimalData();
    fetchBases();
    fetchMedicationCatalog();
  }, [id]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab) {
      setCurrentRecordTab(requestedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (frequencyMode === 'times_per_day') {
      setSpecificTimesInput((current) => normalizeTimesPerDayInput(current, frequencyValue));
    }
  }, [frequencyMode, frequencyValue]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchMedicationCatalog = async () => {
    try {
      const response = await axios.get(`${API}/medication-catalog`);
      setMedicationCatalog(response.data || []);
    } catch (error) {
      console.error('Error fetching medication catalog:', error);
    }
  };

  const fetchDailyRecordsPage = async (page = 1, append = false) => {
    if (!canViewDailyHistory) {
      setDailyRecords([]);
      setDailyRecordsPage(1);
      setHasMoreDailyRecords(false);
      return;
    }

    const recordsResponse = await axios.get(`${API}/animals/${id}/daily-records`, {
      params: { page, limit: DAILY_RECORDS_PAGE_SIZE }
    });
    const nextRecords = recordsResponse.data || [];
    setDailyRecords((currentRecords) => {
      if (!append) return nextRecords;
      const existingIds = new Set(currentRecords.map((record) => record.id));
      return [
        ...currentRecords,
        ...nextRecords.filter((record) => !existingIds.has(record.id))
      ];
    });
    setDailyRecordsPage(page);
    setHasMoreDailyRecords(nextRecords.length === DAILY_RECORDS_PAGE_SIZE);
  };

  const fetchAnimalData = async () => {
    setLoading(true);
    try {
      const animalResponse = await axios.get(`${API}/admissions/${id}`);
      setAnimal(animalResponse.data);
      await fetchDailyRecordsPage(1, false);
      const medResponse = await axios.get(`${API}/animals/${id}/medication-protocols`);
      setMedProtocols(medResponse.data);
      
      // Fetch executions for each protocol
      const executionsMap = {};
      for (const protocol of medResponse.data) {
        try {
          const execResponse = await axios.get(`${API}/protocols/${protocol.id}/executions`);
          executionsMap[protocol.id] = execResponse.data;
        } catch (err) {
          executionsMap[protocol.id] = [];
        }
      }
      setProtocolExecutions(executionsMap);
      
      const physioResponse = await axios.get(`${API}/animals/${id}/physiotherapy-protocols`);
      setPhysioProtocols(physioResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const refreshAnimalSummary = async () => {
    try {
      const response = await axios.get(`${API}/admissions/${id}`);
      setAnimal(response.data);
    } catch (error) {
      toast.error('Não foi possível atualizar os dados do animal');
    }
  };

  const handleLoadOlderDailyRecords = async () => {
    setLoadingMoreDailyRecords(true);
    try {
      await fetchDailyRecordsPage(dailyRecordsPage + 1, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar históricos antigos');
    } finally {
      setLoadingMoreDailyRecords(false);
    }
  };

  const handleSubmitDailyRecord = async (e) => {
    e.preventDefault();
    if (savingDailyRecord) return;

    if (!recordDate || !recordTime) {
      toast.error('Data e horário do acompanhamento são obrigatórios');
      return;
    }
    
    if (!clinicalEvolution || !prognosis) {
      toast.error('Evolucao clinica e prognostico sao obrigatorios');
      return;
    }
    
    const hasAnyParam = temperature || glucose || weight || appetite || behavior || hydration;
    if (!hasAnyParam) {
      toast.error('Preencha pelo menos um parametro fisiologico');
      return;
    }
    
    setSavingDailyRecord(true);
    try {
      const physiologicalParams = {
        temperature: parseOptionalDecimal(temperature, 'Temperatura'),
        glucose: parseOptionalDecimal(glucose, 'Glicose'),
        weight: parseOptionalDecimal(weight, 'Peso'),
        appetite,
        behavior,
        hydration
      };
      const response = await axios.post(`${API}/animals/${id}/daily-records`, {
        admission_id: id,
        client_request_id: dailyRecordClientRequestId,
        date: buildRecordDateTimeIso(recordDate, recordTime),
        physiological_params: physiologicalParams,
        clinical_evolution: clinicalEvolution,
        prognosis: prognosis,
        requested_exam_types: enableLabAlert ? labExamRequests.map((item) => item.exam_type.trim()).filter(Boolean) : [],
        lab_exam_requests: enableLabAlert
          ? labExamRequests
            .map((item) => ({
              exam_type: item.exam_type.trim(),
              laboratory_type: item.laboratory_type || 'interno'
            }))
            .filter((item) => item.exam_type)
          : [],
        lab_alert_notes: enableLabAlert ? (labAlertNotes || null) : null
      });

      let mediaUploadFailed = false;
      if (dailyMediaFiles.length > 0) {
        for (const file of dailyMediaFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/daily-records/${response.data.id}/photos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            mediaUploadFailed = true;
            console.error('Error uploading daily record media:', uploadError);
          }
        }
      }

      if (mediaUploadFailed) {
        toast.error('Registro salvo, mas um ou mais anexos não foram enviados');
      } else {
        toast.success('Registro salvo!');
      }
      setTemperature(''); setGlucose(''); setWeight('');
      setAppetite(''); setBehavior(''); setHydration('');
      setClinicalEvolution(''); setPrognosis('');
      setRecordDate(toDateInputValue()); setRecordTime(toTimeInputValue());
      setRequestedExamTypes(''); setLabExamRequests([{ exam_type: '', laboratory_type: 'interno' }]); setLabAlertNotes(''); setEnableLabAlert(false); setDailyMediaFiles([]);
      setDailyRecordClientRequestId(createClientRequestId('daily-record'));
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erro ao salvar');
    } finally {
      setSavingDailyRecord(false);
    }
  };

  const handleAppetiteChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingAppetite(true);
      setAppetite('');
      return;
    }
    setCreatingAppetite(false);
    setAppetite(value);
  };

  const handleBehaviorChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingBehavior(true);
      setBehavior('');
      return;
    }
    setCreatingBehavior(false);
    setBehavior(value);
  };

  const handleHydrationChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingHydration(true);
      setHydration('');
      return;
    }
    setCreatingHydration(false);
    setHydration(value);
  };

  const handlePrognosisChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingPrognosis(true);
      setPrognosis('');
      return;
    }
    setCreatingPrognosis(false);
    setPrognosis(value);
  };

  const applyMedicationCatalogItem = (item) => {
    setSelectedMedicationCatalogId(item.id);
    setMedicationName(item.active_ingredient || '');
    setPresentation(item.presentations?.[0] || '');
    setDosage(item.suggested_doses?.[0] || '');
    setRoute(item.routes?.[0] || '');
    setCreatingPresentation(false);
    setCreatingRoute(false);
  };

  const handleMedicationNameChange = (value) => {
    setMedicationName(value);
    const matchedItem = medicationCatalog.find((item) => normalizeOptionText(item.active_ingredient) === normalizeOptionText(value));
    if (matchedItem) {
      applyMedicationCatalogItem(matchedItem);
      return;
    }
    setSelectedMedicationCatalogId('');
  };

  const handlePresentationChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingPresentation(true);
      setPresentation('');
      return;
    }
    setCreatingPresentation(false);
    setPresentation(value);
  };

  const handleRouteChange = (value) => {
    if (value === CREATE_NEW_VALUE) {
      setCreatingRoute(true);
      setRoute('');
      return;
    }
    setCreatingRoute(false);
    setRoute(value);
  };

  const resetMedicationProtocolForm = () => {
    setEditingMedicationProtocol(null);
    setTreatmentProtocolType('medication');
    setSelectedMedicationCatalogId('');
    setMedicationName('');
    setTherapyType('Fisioterapia');
    setTherapyDuration('');
    setPresentation('');
    setDosage('');
    setRoute('');
    setCreatingPresentation(false);
    setCreatingRoute(false);
    setFrequency('');
    setFrequencyMode('interval_hours');
    setFrequencyValue('8');
    setSpecificTimesInput('');
    setProtocolStartDate(toDateInputValue(new Date()));
    setProtocolEndDate('');
    setStartTime('08:00');
    setDurationDays('');
    setProtocolEditReason('');
    setMedProtocolClientRequestId(createClientRequestId('med-protocol'));
  };

  const openNewMedicationProtocolForm = () => {
    resetMedicationProtocolForm();
    setShowMedForm(true);
  };

  const openEditMedicationProtocolForm = (protocol) => {
    const nextProtocolType = protocol.protocol_type === 'therapy' ? 'therapy' : 'medication';
    setEditingMedicationProtocol(protocol);
    setTreatmentProtocolType(nextProtocolType);
    setSelectedMedicationCatalogId(protocol.medication_catalog_id || '');
    setMedicationName(nextProtocolType === 'therapy' ? (protocol.notes || '') : (protocol.active_ingredient || protocol.medication_name || ''));
    setTherapyType(protocol.therapy_type || 'Fisioterapia');
    setTherapyDuration(protocol.therapy_duration || '');
    setPresentation(protocol.presentation || '');
    setDosage(protocol.dosage || '');
    setRoute(protocol.route || '');
    setCreatingPresentation(false);
    setCreatingRoute(false);
    setFrequency(protocol.frequency || '');
    setFrequencyMode(protocol.frequency_mode || 'interval_hours');
    setFrequencyValue(protocol.frequency_value ? String(protocol.frequency_value) : '8');
    setSpecificTimesInput((protocol.specific_times || []).join(', '));
    setProtocolStartDate(toDateInputValue(protocol.start_date || new Date()));
    setProtocolEndDate(protocol.end_date ? toDateInputValue(protocol.end_date) : '');
    setStartTime(protocol.start_time || '08:00');
    setDurationDays(protocol.duration_days ? String(protocol.duration_days) : '');
    setProtocolEditReason('');
    setShowMedForm(true);
  };

  const closeMedicationProtocolForm = () => {
    resetMedicationProtocolForm();
    setShowMedForm(false);
  };

  const requestOutcomeProtocolConfirmation = (actionLabel, options = {}) => {
    const { force = false, reason = 'Este registro exige confirmação' } = options;
    if (!animalHasOutcome && !force) return null;
    const typed = window.prompt(
      `${animalHasOutcome ? 'Este animal possui desfecho registrado' : reason}. Para ${actionLabel}, digite ${OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD}.`
    );
    if (typed === null) return false;
    if (typed.trim().toUpperCase() !== OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD) {
      toast.error(`Confirmação inválida. Digite ${OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD} para continuar.`);
      return false;
    }
    return OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD;
  };

  const protocolNeedsRetroactiveExecutionConfirmation = (protocol, executionDateValue) => {
    if (!protocol) return { required: false, reason: '' };
    if (protocol.status === 'cancelled' || protocol.cancelled_at) {
      return { required: true, reason: 'Este protocolo está cancelado' };
    }
    if (protocol.status === 'suspended' || protocol.suspended_at || protocol.active === false) {
      return { required: true, reason: 'Este protocolo está suspenso ou encerrado' };
    }
    const executionDay = executionDateValue || toDateInputValue(new Date());
    const endDay = protocol.end_date ? toDateInputValue(protocol.end_date) : '';
    if (endDay && executionDay > endDay) {
      return { required: true, reason: 'Este protocolo está fora do prazo de vigência' };
    }
    return { required: false, reason: '' };
  };

  const handleSubmitMedProtocol = async (e) => {
    e.preventDefault();
    if (savingMedicationProtocol) return;
    
    const parsedSpecificTimes = specificTimesInput
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    const builtFrequency = buildProtocolFrequencyLabel(frequencyMode, Number(frequencyValue), parsedSpecificTimes, frequency);

    const protocolIsTherapy = treatmentProtocolType === 'therapy';
    if (!protocolIsTherapy && (!medicationName || !dosage || !route || !builtFrequency)) {
      toast.error('Medicamento, dose, via e frequência são obrigatórios');
      return;
    }

    if (protocolIsTherapy && (!therapyType || !builtFrequency)) {
      toast.error('Tipo de terapia e frequência são obrigatórios');
      return;
    }

    if (editingMedicationProtocol && protocolEditReason.trim().length < 5) {
      toast.error('Informe o motivo da correção do protocolo');
      return;
    }
    const outcomeConfirmation = !editingMedicationProtocol
      ? requestOutcomeProtocolConfirmation('registrar protocolo retroativo neste prontuário')
      : null;
    if (outcomeConfirmation === false) return;

    const calculatedDurationDays = protocolStartDate && protocolEndDate
      ? Math.max(1, Math.floor((new Date(`${protocolEndDate}T00:00:00`) - new Date(`${protocolStartDate}T00:00:00`)) / 86400000) + 1)
      : null;
    const payload = {
      admission_id: id,
      protocol_type: treatmentProtocolType,
      medication_catalog_id: protocolIsTherapy ? null : selectedMedicationCatalogId || null,
      active_ingredient: protocolIsTherapy ? null : medicationName,
      medication_name: protocolIsTherapy ? therapyType : medicationName,
      therapy_type: protocolIsTherapy ? therapyType : null,
      therapy_duration: protocolIsTherapy ? therapyDuration || null : null,
      presentation: protocolIsTherapy ? null : presentation || null,
      dosage: protocolIsTherapy ? null : dosage,
      route: protocolIsTherapy ? null : route,
      frequency: builtFrequency,
      frequency_mode: frequencyMode,
      frequency_value: frequencyMode === 'sos' || frequencyMode === 'specific_times' ? null : parseInt(frequencyValue, 10),
      specific_times: ['specific_times', 'times_per_day'].includes(frequencyMode) ? parsedSpecificTimes : [],
      start_date: new Date(`${protocolStartDate}T00:00:00`).toISOString(),
      end_date: protocolEndDate ? new Date(`${protocolEndDate}T23:59:59`).toISOString() : null,
      start_time: startTime || '08:00',
      duration_days: calculatedDurationDays || (durationDays ? parseInt(durationDays) : null),
      notes: protocolIsTherapy && medicationName ? medicationName : null
    };
    if (outcomeConfirmation) {
      payload.outcome_override_confirmation = outcomeConfirmation;
    }
    if (!editingMedicationProtocol) {
      payload.client_request_id = medProtocolClientRequestId;
    }

    setSavingMedicationProtocol(true);
    try {
      if (editingMedicationProtocol) {
        await axios.put(`${API}/medication-protocols/${editingMedicationProtocol.id}`, {
          ...payload,
          update_reason: protocolEditReason.trim()
        });
        toast.success('Protocolo de tratamento atualizado!');
      } else {
        await axios.post(`${API}/animals/${id}/medication-protocols`, payload);
        toast.success('Protocolo de tratamento criado!');
        setMedProtocolClientRequestId(createClientRequestId('med-protocol'));
      }
      closeMedicationProtocolForm();
      fetchMedicationCatalog();
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel salvar o protocolo. Tente novamente.');
    } finally {
      setSavingMedicationProtocol(false);
    }
  };

  const openExecutionDialog = (protocol, execution = null) => {
    const scheduledHours = parseProtocolHours(protocol);
    setExecutionProtocol(protocol);
    setEditingExecution(execution);
    setExecutionScheduledHour(
      execution?.scheduled_hour != null
        ? String(execution.scheduled_hour)
        : (scheduledHours[0] != null ? String(scheduledHours[0]) : '')
    );
    setExecutionStatus(execution?.status || 'realizado');
    setExecutionNotes(execution?.notes || '');
    setExecutionDate(toDateInputValue(execution?.execution_date || new Date()));
    setExecutionTime(toTimeInputValue(execution?.execution_date || new Date()));
    setExecutionDateTimeJustification(execution?.date_time_justification || execution?.update_reason || '');
    setExecutionMediaFile(null);
    setExecutionClientRequestId(createClientRequestId('protocol-execution'));
    setShowExecutionDialog(true);
  };

  const handleSaveExecution = async () => {
    if (!executionProtocol) return;
    const retroactiveRule = protocolNeedsRetroactiveExecutionConfirmation(executionProtocol, executionDate);
    const outcomeConfirmation = !editingExecution?.id
      ? requestOutcomeProtocolConfirmation(
        'registrar execução/protocolo retroativo neste prontuário',
        {
          force: retroactiveRule.required,
          reason: retroactiveRule.reason
        }
      )
      : null;
    if (outcomeConfirmation === false) return;
    const payload = {
      client_request_id: editingExecution?.id ? undefined : executionClientRequestId,
      status: executionStatus,
      notes: executionNotes || null,
      scheduled_hour: executionScheduledHour !== '' ? parseInt(executionScheduledHour, 10) : null,
      ...(canSetProtocolExecutionDateTime(user) ? {
        execution_date: executionDate || null,
        execution_time: executionTime || null,
        date_time_justification: executionDateTimeJustification || null,
        update_reason: executionDateTimeJustification || null
      } : {})
    };
    if (outcomeConfirmation) {
      payload.outcome_override_confirmation = outcomeConfirmation;
    }

    setSavingExecution(true);
    try {
      if (editingExecution?.id) {
        await axios.put(`${API}/protocol-executions/${editingExecution.id}`, payload);
        toast.success('Execução atualizada!');
      } else {
        const response = await axios.post(`${API}/protocols/${executionProtocol.id}/execute?protocol_type=medication`, payload);
        setExecutionClientRequestId(createClientRequestId('protocol-execution'));
        let uploadedMedia = false;
        let mediaUploadFailed = false;
        if (executionMediaFile && response.data?.id) {
          const formData = new FormData();
          formData.append('file', executionMediaFile);
          formData.append('caption', executionMediaFile.name);
          try {
            await axios.post(`${API}/protocol-executions/${response.data.id}/media`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            uploadedMedia = true;
          } catch (uploadError) {
            mediaUploadFailed = true;
            console.error('Error uploading protocol execution media:', uploadError);
          }
        }
        if (mediaUploadFailed) {
          toast.error('Protocolo executado, mas a foto não foi enviada');
        } else {
          toast.success(uploadedMedia ? 'Protocolo executado com foto!' : 'Protocolo executado e registrado!');
        }
      }
      setShowExecutionDialog(false);
      setEditingExecution(null);
      setExecutionProtocol(null);
      setExecutionMediaFile(null);
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar execução');
    } finally {
      setSavingExecution(false);
    }
  };

  const handleDeleteExecution = async (executionId) => {
    const reason = window.prompt('Informe o motivo para cancelar esta execução. O histórico será preservado.');
    if (!reason?.trim()) {
      toast.error('Motivo obrigatório para cancelar a execução');
      return;
    }
    try {
      await axios.delete(`${API}/protocol-executions/${executionId}?reason=${encodeURIComponent(reason.trim())}`);
      toast.success('Execução cancelada!');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir execução');
    }
  };

  const handleDeleteMedicationProtocol = async (protocol) => {
    const reason = window.prompt(`Informe o motivo para cancelar o protocolo "${protocol.medication_name}". O histórico será preservado.`);
    if (!reason?.trim()) {
      toast.error('Motivo obrigatório para cancelar o protocolo');
      return;
    }
    try {
      await axios.post(`${API}/medication-protocols/${protocol.id}/cancel`, { reason: reason.trim() });
      toast.success('Protocolo de tratamento cancelado');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir protocolo');
    }
  };

  const handleSuspendMedicationProtocol = async (protocol) => {
    const reason = window.prompt(`Informe o motivo para suspender o protocolo "${protocol.medication_name}".`);
    if (!reason?.trim()) {
      toast.error('Motivo obrigatório para suspender o protocolo');
      return;
    }
    try {
      await axios.post(`${API}/medication-protocols/${protocol.id}/suspend`, { reason: reason.trim() });
      toast.success('Protocolo de tratamento suspenso');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao suspender protocolo');
    }
  };

  const handlePermanentDeleteMedicationProtocol = async (protocol) => {
    const label = protocol.protocol_type === 'therapy'
      ? (protocol.therapy_type || protocol.medication_name || 'protocolo de terapia')
      : (protocol.medication_name || 'protocolo');
    const confirmation = window.prompt(`Esta ação exclui permanentemente o protocolo "${label}" e suas execuções. Digite EXCLUIR para confirmar.`);
    if (normalizeConfirmationText(confirmation).toUpperCase() !== 'EXCLUIR') {
      toast.error('Confirmação inválida. Digite EXCLUIR para excluir permanentemente.');
      return;
    }
    try {
      await axios.delete(`${API}/medication-protocols/${protocol.id}/permanent`, {
        data: { confirm_text: 'EXCLUIR' }
      });
      toast.success('Protocolo excluído permanentemente');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir protocolo permanentemente');
    }
  };

  const handleDeleteAnimal = async () => {
    if (!animal) return;
    const expectedConfirmation = normalizeConfirmationText(animal.ibama_id);
    const confirmation = window.prompt(`Digite ${expectedConfirmation} para excluir este animal e todos os registros vinculados.`);
    if (normalizeConfirmationText(confirmation) !== expectedConfirmation) {
      toast.error(`Confirmação inválida. Digite exatamente: ${expectedConfirmation}`);
      return;
    }
    try {
      await axios.delete(`${API}/animals/${animal.id}`);
      toast.success('Animal e registros vinculados excluídos com sucesso');
      navigate(animalSearchReturnTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir animal');
    }
  };

  const handleExecutionMediaUpload = async (executionId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', file.name);

    setUploadingExecutionId(executionId);
    try {
      await axios.post(`${API}/protocol-executions/${executionId}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Mídia anexada à execução');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mídia da execução');
    } finally {
      setUploadingExecutionId(null);
    }
  };

  const handleDeleteExecutionMedia = async (executionId, mediaItem) => {
    if (!mediaItem?.file_url) return;
    if (!window.confirm('Excluir esta mídia da execução?')) return;
    try {
      await axios.delete(`${API}/protocol-executions/${executionId}/media`, {
        params: { file_url: mediaItem.file_url }
      });
      toast.success('Mídia da execução excluída');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir mídia da execução');
    }
  };

  const handleDownloadDetailedReport = async () => {
    setDownloadingDetailedReport(true);
    try {
      const response = await axios.get(`${API}/animals/${id}/detailed-report/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_individual_${animal?.ibama_id || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório individual baixado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar relatório individual');
    } finally {
      setDownloadingDetailedReport(false);
    }
  };

  const handleDownloadLabel = async () => {
    setDownloadingLabel(true);
    try {
      const response = await axios.get(`${API}/animals/${id}/label/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etiqueta_${animal?.ibama_id || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Etiqueta com QR Code baixada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar etiqueta');
    } finally {
      setDownloadingLabel(false);
    }
  };

  const handleDownloadEnclosureLabel = async () => {
    setDownloadingEnclosureLabel(true);
    try {
      const response = await axios.get(`${API}/animals/${id}/enclosure-label/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recinto_${animal?.ibama_id || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Etiqueta de recinto baixada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar etiqueta de recinto');
    } finally {
      setDownloadingEnclosureLabel(false);
    }
  };

  const getLastExecution = (protocolId) => {
    const executions = protocolExecutions[protocolId] || [];
    if (executions.length === 0) return null;
    return executions[0];
  };

  const dailyRecordGroups = dailyRecords
    .reduce((groups, record) => {
      const dateValue = getRecordDateValue(record);
      const groupKey = toDateInputValue(dateValue) || 'sem-data';
      const existingGroup = groups.find((group) => group.key === groupKey);
      if (existingGroup) {
        existingGroup.records.push(record);
      } else {
        groups.push({
          key: groupKey,
          label: formatRecordDate(dateValue),
          records: [record]
        });
      }
      return groups;
    }, [])
    .map((group) => ({
      ...group,
      records: group.records
        .slice()
        .sort((a, b) => new Date(getRecordDateValue(b)).getTime() - new Date(getRecordDateValue(a)).getTime())
    }));

  const toggleDailyRecordDate = (dateKey) => {
    setExpandedDailyRecordDates((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  const toggleDailyRecord = (recordId) => {
    setExpandedDailyRecords((prev) => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  const openDailyRecordEdit = (record) => {
    const dateValue = getRecordDateValue(record);
    setEditingDailyRecord(record);
    setDailyRecordForm({
      record_date: toDateInputValue(dateValue),
      record_time: toTimeInputValue(dateValue),
      temperature: record.physiological_params?.temperature ?? '',
      glucose: record.physiological_params?.glucose ?? '',
      weight: record.physiological_params?.weight ?? '',
      appetite: record.physiological_params?.appetite || '',
      behavior: record.physiological_params?.behavior || '',
      hydration: record.physiological_params?.hydration || '',
      clinical_evolution: record.clinical_evolution || '',
      prognosis: record.prognosis || '',
      requested_exam_types: record.requested_exam_types?.join(', ') || '',
      lab_alert_notes: record.lab_alert_notes || ''
    });
  };

  const updateDailyRecordForm = (field, value) => {
    setDailyRecordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDailyRecordEdit = async () => {
    if (!editingDailyRecord) return;
    if (!dailyRecordForm.record_date || !dailyRecordForm.record_time) {
      toast.error('Data e horário do acompanhamento são obrigatórios');
      return;
    }
    if (!dailyRecordForm.clinical_evolution || !dailyRecordForm.prognosis) {
      toast.error('Evolução clínica e prognóstico são obrigatórios');
      return;
    }

    setSavingDailyRecordEdit(true);
    try {
      const recordDateTimeIso = buildRecordDateTimeIso(dailyRecordForm.record_date, dailyRecordForm.record_time);
      if (!recordDateTimeIso) {
        throw new Error('Data e horário do acompanhamento devem ser válidos');
      }
      await axios.put(`${API}/daily-records/${editingDailyRecord.id}`, {
        date: recordDateTimeIso,
        physiological_params: {
          temperature: parseOptionalDecimal(dailyRecordForm.temperature, 'Temperatura'),
          glucose: parseOptionalDecimal(dailyRecordForm.glucose, 'Glicose'),
          weight: parseOptionalDecimal(dailyRecordForm.weight, 'Peso'),
          appetite: dailyRecordForm.appetite,
          behavior: dailyRecordForm.behavior,
          hydration: dailyRecordForm.hydration
        },
        clinical_evolution: dailyRecordForm.clinical_evolution,
        prognosis: dailyRecordForm.prognosis,
        requested_exam_types: dailyRecordForm.requested_exam_types.split(',').map((item) => item.trim()).filter(Boolean),
        lab_alert_notes: dailyRecordForm.lab_alert_notes || null
      });
      toast.success('Histórico atualizado com auditoria');
      setEditingDailyRecord(null);
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar histórico');
    } finally {
      setSavingDailyRecordEdit(false);
    }
  };

  const updateLabExamRequest = (index, field, value) => {
    setLabExamRequests((prev) => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const addLabExamRequest = () => {
    setLabExamRequests((prev) => [...prev, { exam_type: '', laboratory_type: 'interno' }]);
  };

  const removeLabExamRequest = (index) => {
    setLabExamRequests((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [{ exam_type: '', laboratory_type: 'interno' }];
    });
  };

  const handleDeleteDailyRecord = async (record) => {
    if (!window.confirm('Excluir este histórico de acompanhamento? A exclusão ficará registrada na auditoria.')) return;
    try {
      await axios.delete(`${API}/daily-records/${record.id}`);
      toast.success('Histórico excluído com auditoria');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir histórico');
    }
  };

  const handleDailyMediaUpload = async (recordId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', file.name);
    try {
      await axios.post(`${API}/daily-records/${recordId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Mídia adicionada ao acompanhamento');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mídia');
    }
  };

  const handleEditDailyMedia = async (recordId, mediaItem) => {
    if (!mediaItem?.file_url) return;
    const caption = window.prompt('Legenda da mídia:', mediaItem.caption || mediaItem.filename || '');
    if (caption === null) return;
    const observation = window.prompt('Observação da mídia:', mediaItem.observation || '');
    if (observation === null) return;
    try {
      await axios.put(`${API}/daily-records/${recordId}/photos`, {
        file_url: mediaItem.file_url,
        caption,
        observation
      });
      toast.success('Mídia atualizada');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar mídia');
    }
  };

  const handleDeleteDailyMedia = async (recordId, mediaItem) => {
    if (!mediaItem?.file_url) return;
    if (!window.confirm('Excluir esta mídia do acompanhamento?')) return;
    try {
      await axios.delete(`${API}/daily-records/${recordId}/photos`, {
        params: { file_url: mediaItem.file_url }
      });
      toast.success('Mídia do acompanhamento excluída');
      fetchAnimalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir mídia');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;
  }

  if (!animal) {
    return <div className="min-h-screen flex items-center justify-center"><Button onClick={() => navigate(animalSearchReturnTo)}>Voltar</Button></div>;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="hidden md:flex md:items-center md:justify-between md:gap-6">
            <div className="flex min-w-0 items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 flex-shrink-0 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="truncate text-xl font-bold text-emerald-800">Prontuário - {animal.ibama_id}</h1>
                <p className="truncate text-sm text-emerald-500">{animal.common_name}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canDeleteAnimal && (
                <Button onClick={handleDeleteAnimal} variant="outline" className="px-3 text-sm text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Animal
                </Button>
              )}
              <Button onClick={handleDownloadLabel} variant="outline" disabled={downloadingLabel} className="px-3 text-sm">
                <QrCode className="w-4 h-4 mr-2" />
                {downloadingLabel ? 'Gerando etiqueta...' : 'Etiqueta QR'}
              </Button>
              <Button onClick={handleDownloadEnclosureLabel} variant="outline" disabled={downloadingEnclosureLabel} className="px-3 text-sm">
                <MapPin className="w-4 h-4 mr-2" />
                {downloadingEnclosureLabel ? 'Gerando recinto...' : 'Etiqueta Recinto'}
              </Button>
              <Button onClick={handleDownloadDetailedReport} variant="outline" disabled={downloadingDetailedReport} className="px-3 text-sm">
                <Download className="w-4 h-4 mr-2" />
                {downloadingDetailedReport ? 'Gerando...' : 'Relatório'}
              </Button>
              <Button onClick={() => navigate(animalSearchReturnTo)} variant="outline" className="px-3 text-sm">Voltar</Button>
              <Button onClick={logout} variant="ghost" className="px-3 text-sm text-red-600">Sair</Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex min-w-0 items-start space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-11 w-11 flex-shrink-0 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="break-words text-lg font-bold text-emerald-800">Prontuário - {animal.ibama_id}</h1>
                <p className="break-words text-xs text-emerald-500">{animal.common_name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {canDeleteAnimal && (
                <Button onClick={handleDeleteAnimal} variant="outline" className="w-full justify-center px-3 text-xs text-red-600 sm:text-sm">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Animal
                </Button>
              )}
              <Button onClick={handleDownloadLabel} variant="outline" disabled={downloadingLabel} className="w-full justify-center px-3 text-xs sm:text-sm">
                <QrCode className="w-4 h-4 mr-2" />
                {downloadingLabel ? 'Gerando etiqueta...' : 'Etiqueta QR'}
              </Button>
              <Button onClick={handleDownloadEnclosureLabel} variant="outline" disabled={downloadingEnclosureLabel} className="w-full justify-center px-3 text-xs sm:text-sm">
                <MapPin className="w-4 h-4 mr-2" />
                {downloadingEnclosureLabel ? 'Gerando recinto...' : 'Etiqueta Recinto'}
              </Button>
              <Button onClick={handleDownloadDetailedReport} variant="outline" disabled={downloadingDetailedReport} className="w-full justify-center px-3 text-xs sm:text-sm">
                <Download className="w-4 h-4 mr-2" />
                {downloadingDetailedReport ? 'Gerando...' : 'Relatório'}
              </Button>
              <Button onClick={() => navigate(animalSearchReturnTo)} variant="outline" className="w-full justify-center px-3 text-xs sm:text-sm">Voltar</Button>
              <Button onClick={logout} variant="ghost" className="w-full justify-center px-3 text-xs text-red-600 sm:text-sm">Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 py-6 md:px-4 md:py-8">
        <Card>
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <Tabs value={currentRecordTab} onValueChange={setCurrentRecordTab}>
              {/* Mobile: Dropdown Select */}
              <div className="block md:hidden mb-4">
                <Label className="text-base font-semibold text-emerald-800 mb-2 block">
                  Seção do Prontuário
                </Label>
                <Select value={currentRecordTab} onValueChange={setCurrentRecordTab}>
                  <SelectTrigger className="w-full min-h-[48px] text-base font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial" className="text-base py-3">
                      📋 Dados Iniciais
                    </SelectItem>
                    <SelectItem value="location" className="text-base py-3">
                      📍 Localização
                    </SelectItem>
                    <SelectItem value="transfers" className="text-base py-3">
                      🔄 Transferências
                    </SelectItem>
                    <SelectItem value="monitoring" className="text-base py-3">
                      📊 Acompanhamento Diário
                    </SelectItem>
                    <SelectItem value="feeding" className="text-base py-3">
                      🍽️ Alimentação
                    </SelectItem>
                    <SelectItem value="protocols" className="text-base py-3">
                      💊 Protocolos de Medicação
                    </SelectItem>
                    <SelectItem value="exams" className="text-base py-3">
                      🔬 Exames Complementares
                    </SelectItem>
                    <SelectItem value="outcome" className="text-base py-3">
                      🏁 Desfecho
                    </SelectItem>
                    <SelectItem value="necropsy" className="text-base py-3">
                      ☠️ Necropsia
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Desktop: Tab Grid */}
              <div className="hidden md:block mb-4">
                <TabsList className="grid w-full grid-cols-9">
                  <TabsTrigger value="initial" className="text-xs">
                    Dados
                  </TabsTrigger>
                  <TabsTrigger value="location" className="text-xs">
                    Local
                  </TabsTrigger>
                  <TabsTrigger value="transfers" className="text-xs">
                    Transfer.
                  </TabsTrigger>
                  <TabsTrigger value="monitoring" className="text-xs">
                    Acompan.
                  </TabsTrigger>
                  <TabsTrigger value="feeding" className="text-xs" data-testid="feeding-tab-trigger">
                    Aliment.
                  </TabsTrigger>
                  <TabsTrigger value="protocols" className="text-xs">
                    Protocolos
                  </TabsTrigger>
                  <TabsTrigger value="exams" className="text-xs">
                    Exames
                  </TabsTrigger>
                  <TabsTrigger value="outcome" className="text-xs">
                    Desfecho
                  </TabsTrigger>
                  <TabsTrigger value="necropsy" className="text-xs">
                    Necropsia
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="initial" className="mt-6">
                {/* Milestones Panel at top */}
                <MilestonesPanel 
                  animalId={id} 
                  animal={animal} 
                  user={user}
                  onUpdate={fetchAnimalData}
                />
                
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="font-semibold text-emerald-800">Animal</h3>
                      {hasPermission(user, 'animals.edit_admin_data') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admissions/${id}`)}
                          className="w-full sm:w-auto"
                        >
                          Editar dados
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>ID IBAMA:</strong> {animal.ibama_id}</p>
                      {animal.pmp_id && <p><strong>ID PMP:</strong> {animal.pmp_id}</p>}
                      <p><strong>Grupo:</strong> {animal.animal_group}</p>
                      <p><strong>Especie:</strong> {animal.species}</p>
                      <p><strong>Nome Popular:</strong> {animal.common_name}</p>
                      <p><strong>Base:</strong> {animal.base_name}</p>
                      <p><strong>Alocacao:</strong> {animal.allocation_location} - {animal.bay_number}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-800 mb-3">Origem</h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Cidade:</strong> {animal.origin_city}</p>
                      <p><strong>Bairro:</strong> {animal.origin_neighborhood}</p>
                      <p><strong>Responsavel:</strong> {animal.responsible.name}</p>
                      <p><strong>Orgao:</strong> {animal.responsible.organ}</p>
                      <p><strong>Data:</strong> {new Date(animal.admission_date).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="location" className="mt-6">
                <MovementsTab 
                  animalId={id} 
                  animal={animal} 
                  user={user}
                  onLocationUpdate={fetchAnimalData}
                />
              </TabsContent>

              <TabsContent value="transfers" className="mt-6">
                <TransfersTab 
                  animalId={id} 
                  animal={animal} 
                  user={user}
                  bases={bases}
                  onTransferComplete={fetchAnimalData}
                />
              </TabsContent>

              <TabsContent value="monitoring" className="mt-6">
                {canViewDailyHistory && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-emerald-800 mb-4">Tendências de Sinais Vitais</h3>
                    <VitalTrendsPanel animalId={id} />
                  </div>
                )}

                {canCreateDailyHistory && (
                  <Card className="mb-6 bg-emerald-50">
                    <CardHeader><CardTitle>Novo Registro Diario</CardTitle></CardHeader>
                    <CardContent>
                      <form onSubmit={handleSubmitDailyRecord} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <Label>Data do acompanhamento *</Label>
                            <Input type="date" value={recordDate} onChange={(e) => setRecordDate(e.target.value)} required />
                          </div>
                          <div>
                            <Label>Horário do acompanhamento *</Label>
                            <Input type="time" value={recordTime} onChange={(e) => setRecordTime(e.target.value)} required />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <div><Label>Temperatura (C)</Label><Input type="number" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)} /></div>
                          <div><Label>Glicose (mg/dL)</Label><Input type="number" step="0.1" value={glucose} onChange={(e) => setGlucose(e.target.value)} /></div>
                          <div><Label>Peso (kg)</Label><Input type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
                          <div>
                            <Label>Apetite</Label>
                            <Select value={creatingAppetite ? CREATE_NEW_VALUE : appetite} onValueChange={handleAppetiteChange}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                {appetiteOptions.map((item) => (
                                  <SelectItem key={item} value={item}>{item}</SelectItem>
                                ))}
                                <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo</SelectItem>
                              </SelectContent>
                            </Select>
                            {creatingAppetite && (
                              <Input
                                className="mt-2"
                                value={appetite}
                                onChange={(e) => setAppetite(e.target.value)}
                                placeholder="Digite o novo apetite"
                              />
                            )}
                          </div>
                          <div>
                            <Label>Comportamento</Label>
                            <Select value={creatingBehavior ? CREATE_NEW_VALUE : behavior} onValueChange={handleBehaviorChange}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                {behaviorOptions.map((item) => (
                                  <SelectItem key={item} value={item}>{item}</SelectItem>
                                ))}
                                <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo</SelectItem>
                              </SelectContent>
                            </Select>
                            {creatingBehavior && (
                              <Input
                                className="mt-2"
                                value={behavior}
                                onChange={(e) => setBehavior(e.target.value)}
                                placeholder="Digite o novo comportamento"
                              />
                            )}
                          </div>
                          <div>
                            <Label>Hidratacao</Label>
                            <Select value={creatingHydration ? CREATE_NEW_VALUE : hydration} onValueChange={handleHydrationChange}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                {hydrationOptions.map((item) => (
                                  <SelectItem key={item} value={item}>{item}</SelectItem>
                                ))}
                                <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo</SelectItem>
                              </SelectContent>
                            </Select>
                            {creatingHydration && (
                              <Input
                                className="mt-2"
                                value={hydration}
                                onChange={(e) => setHydration(e.target.value)}
                                placeholder="Digite a nova hidratação"
                              />
                            )}
                          </div>
                        </div>
                        <div><Label>Evolucao Clinica *</Label><Textarea value={clinicalEvolution} onChange={(e) => setClinicalEvolution(e.target.value)} required rows={4} /></div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={enableLabAlert}
                              onChange={(e) => setEnableLabAlert(e.target.checked)}
                            />
                            Gerar alerta laboratorial
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Cada exame gera uma solicitação independente na Central do Laboratório.</p>
                        </div>
                        {enableLabAlert && (
                          <>
                            <div>
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <Label>Solicitações de exames</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addLabExamRequest}>
                                  + Exame
                                </Button>
                              </div>
                              <div className="space-y-3">
                                {labExamRequests.map((item, index) => (
                                  <div key={`lab-exam-request-${index}`} className="grid gap-2 rounded border bg-white p-3 sm:grid-cols-[1fr_180px_auto]">
                                    <Input
                                      value={item.exam_type}
                                      onChange={(e) => updateLabExamRequest(index, 'exam_type', e.target.value)}
                                      placeholder="Tipo de exame (separe vários por vírgula)"
                                    />
                                    <Select
                                      value={item.laboratory_type}
                                      onValueChange={(value) => updateLabExamRequest(index, 'laboratory_type', value)}
                                    >
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="interno">Interno</SelectItem>
                                        <SelectItem value="externo">Externo</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Button type="button" variant="outline" size="sm" onClick={() => removeLabExamRequest(index)}>
                                      Remover
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Label>Observação para laboratório</Label>
                              <Textarea value={labAlertNotes} onChange={(e) => setLabAlertNotes(e.target.value)} rows={2} />
                            </div>
                          </>
                        )}
                        <div>
                          <Label>Anexar imagens / vídeos do registro</Label>
                          <CameraFileInput
                            accept="image/*,video/*"
                            multiple
                            onFilesSelected={(files) => setDailyMediaFiles((prev) => [...prev, ...files])}
                            fileLabel="Selecionar fotos/vídeos"
                            cameraLabel="Tirar foto"
                          />
                          {dailyMediaFiles.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">{dailyMediaFiles.length} arquivo(s) pronto(s) para envio com o registro.</p>
                          )}
                        </div>
                        <div>
                          <Label>Prognostico *</Label>
                          <Select value={creatingPrognosis ? CREATE_NEW_VALUE : prognosis} onValueChange={handlePrognosisChange} required>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {prognosisOptions.map((item) => (
                                <SelectItem key={item} value={item}>{item}</SelectItem>
                              ))}
                              <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar novo</SelectItem>
                            </SelectContent>
                          </Select>
                          {creatingPrognosis && (
                            <Input
                              className="mt-2"
                              value={prognosis}
                              onChange={(e) => setPrognosis(e.target.value)}
                              placeholder="Digite o novo prognóstico"
                            />
                          )}
                        </div>
                        <Button 
                          type="submit" 
                          disabled={savingDailyRecord}
                          className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px] text-base font-semibold mt-4"
                        >
                          {savingDailyRecord ? 'Salvando...' : 'Salvar Registro'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {canViewDailyHistory && (
                  <>
                    <h3 className="font-semibold text-emerald-800 mb-4">Historico</h3>
                    {dailyRecords.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Nenhum registro</p>
                    ) : (
                  <div className="space-y-4">
                    {dailyRecordGroups.map((group) => (
                      <Card key={group.key}>
                        <CardContent className="pt-4">
                          <button
                            type="button"
                            onClick={() => toggleDailyRecordDate(group.key)}
                            className="flex w-full items-center justify-between gap-3 text-left"
                            data-testid={`toggle-daily-record-date-${group.key}`}
                          >
                            <div className="flex items-center gap-2">
                              {expandedDailyRecordDates[group.key] ? (
                                <ChevronDown className="h-5 w-5 text-emerald-700" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-emerald-700" />
                              )}
                              <div>
                                <p className="font-semibold text-emerald-900">{group.label}</p>
                                <p className="text-sm text-gray-500">{group.records.length} acompanhamento(s)</p>
                              </div>
                            </div>
                          </button>

                          {expandedDailyRecordDates[group.key] && (
                            <div className="mt-4 space-y-3 border-t pt-4">
                              {group.records.map((record) => (
                                <div key={record.id} className="rounded-md border border-gray-200 bg-white p-3">
                                  <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="space-y-2">
                                      <p className="font-semibold">{formatRecordTime(getRecordDateValue(record))}</p>
                                      {(record.lab_requests?.length > 0 || record.requested_exam_types?.length > 0) && (
                                        <Badge className={getLabRequestBadgeClass(record.lab_request_status)}>
                                          Laboratório: {(record.lab_request_status || 'solicitado').replaceAll('_', ' ')}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      {canEditDailyHistory && (
                                        <Button type="button" variant="outline" size="sm" onClick={() => openDailyRecordEdit(record)}>
                                          <Pencil className="mr-2 h-4 w-4" />
                                          Editar
                                        </Button>
                                      )}
                                      {canDeleteDailyHistory && (
                                        <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteDailyRecord(record)}>
                                          <Trash2 className="mr-2 h-4 w-4" />
                                          Excluir
                                        </Button>
                                      )}
                                      <Badge className={record.prognosis === 'Favoravel' ? 'bg-green-500' : 'bg-yellow-500'}>{record.prognosis}</Badge>
                                    </div>
                                  </div>
                                  {record.physiological_params && (
                                    <div className="mb-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                                      {record.physiological_params.temperature && <div className="bg-emerald-50 p-2 rounded"><p>Temp: {record.physiological_params.temperature}C</p></div>}
                                      {record.physiological_params.glucose && <div className="bg-emerald-50 p-2 rounded"><p>Glicose: {record.physiological_params.glucose}</p></div>}
                                      {record.physiological_params.weight && <div className="bg-emerald-50 p-2 rounded"><p>Peso: {record.physiological_params.weight}kg</p></div>}
                                    </div>
                                  )}
                                  <div className="bg-gray-50 p-3 rounded"><p className="text-sm">{record.clinical_evolution}</p></div>
                                  <div className="mt-3">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleDailyRecord(record.id)}
                                      data-testid={`toggle-daily-record-${record.id}`}
                                    >
                                      {expandedDailyRecords[record.id] ? 'Ocultar detalhes' : 'Ver todos os dados'}
                                    </Button>
                                  </div>
                                  {expandedDailyRecords[record.id] && (
                                    <div className="mt-3 p-3 bg-emerald-50 rounded border border-emerald-100 text-sm space-y-1">
                                      <p><strong>Data do acompanhamento:</strong> {formatRecordDate(getRecordDateValue(record))}</p>
                                      <p><strong>Horário do acompanhamento:</strong> {formatRecordTime(getRecordDateValue(record))}</p>
                                      <p><strong>Cadastrado em:</strong> {record.created_at ? new Date(record.created_at).toLocaleString('pt-BR') : 'N/A'}</p>
                                      <p><strong>Cadastrado por:</strong> {record.created_by_name || record.created_by || 'N/A'}</p>
                                      {(record.lab_requests?.length > 0 || record.requested_exam_types?.length > 0) && (
                                        <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-3">
                                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <p className="font-medium text-amber-900">Alerta laboratorial gerado neste acompanhamento</p>
                                            <Badge className={getLabRequestBadgeClass(record.lab_request_status)}>
                                              {record.lab_request_status ? record.lab_request_status.replaceAll('_', ' ') : 'solicitado'}
                                            </Badge>
                                          </div>
                                          {record.lab_requests?.length > 0 ? (
                                            <div className="mt-2 space-y-2">
                                              {record.lab_requests.map((requestItem) => (
                                                <div key={requestItem.id} className="rounded border border-amber-200 bg-white p-2">
                                                  <p>
                                                    <strong>{requestItem.request_number || 'Sem número'}</strong> • {requestItem.exam_type || requestItem.requested_exam_types?.join(', ') || 'Exame'} • {requestItem.laboratory_type === 'externo' ? 'Externo' : 'Interno'}
                                                  </p>
                                                  <p>Status: {String(requestItem.status || 'solicitado').replaceAll('_', ' ')}</p>
                                                  <p>Solicitante: {requestItem.requester_name || requestItem.created_by_name || 'N/A'}</p>
                                                  {requestItem.result_text && <p><strong>Resultado:</strong> {requestItem.result_text}</p>}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="mt-2"><strong>Exames solicitados:</strong> {record.requested_exam_types.join(', ')}</p>
                                          )}
                                          {record.lab_alert_notes && <p><strong>Obs. laboratório:</strong> {record.lab_alert_notes}</p>}
                                          {record.lab_request_updates?.length > 0 && (
                                            <div className="mt-2 space-y-1 text-xs text-gray-600">
                                              {record.lab_request_updates.slice().reverse().slice(0, 3).map((update, index) => (
                                                <p key={`${record.id}-lab-update-${index}`}>
                                                  <strong>{update.status}</strong> • {update.updated_by_name || 'Sistema'} • {update.updated_at ? new Date(update.updated_at).toLocaleString('pt-BR') : 'N/A'}
                                                  {update.note ? ` • ${update.note}` : ''}
                                                </p>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <p><strong>Apetite:</strong> {record.physiological_params?.appetite || 'N/A'}</p>
                                      <p><strong>Comportamento:</strong> {record.physiological_params?.behavior || 'N/A'}</p>
                                      <p><strong>Hidratação:</strong> {record.physiological_params?.hydration || 'N/A'}</p>
                                      <p><strong>Prognóstico:</strong> {record.prognosis || 'N/A'}</p>
                                      <p><strong>Evolução Clínica:</strong> {record.clinical_evolution || 'N/A'}</p>
                                      {record.photos?.length > 0 && (
                                        <div className="pt-2">
                                          <p className="mb-2 font-medium"><strong>Mídias do acompanhamento:</strong></p>
                                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {record.photos.map((photo, index) => (
                                              <div
                                                key={`${record.id}-photo-${index}`}
                                                className="flex items-center justify-between gap-2 rounded border border-emerald-200 bg-white p-2 text-left hover:bg-emerald-50"
                                              >
                                                <button
                                                  type="button"
                                                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                                  onClick={(event) => handleOpenMediaFile(event, photo)}
                                                >
                                                  <ProtectedMediaThumbnail
                                                    media={photo}
                                                    alt={photo.caption || photo.filename || `Mídia ${index + 1}`}
                                                    className="h-14 w-20"
                                                    fallbackClassName="h-14 w-20"
                                                  />
                                                  <span className="min-w-0">
                                                    <span className="block truncate font-medium">{photo.caption || photo.filename || `Mídia ${index + 1}`}</span>
                                                    {photo.observation && <span className="block truncate text-xs text-gray-500">{photo.observation}</span>}
                                                    {(photo.uploaded_by_name || photo.uploaded_at) && (
                                                      <span className="block truncate text-xs text-gray-500">
                                                        {photo.uploaded_by_name || ''}
                                                        {photo.uploaded_at ? `${photo.uploaded_by_name ? ' • ' : ''}${new Date(photo.uploaded_at).toLocaleString('pt-BR')}` : ''}
                                                      </span>
                                                    )}
                                                  </span>
                                                </button>
                                                <span className="text-xs text-gray-500">{photo.file_type === 'video' ? 'Vídeo' : 'Foto'}</span>
                                                {canManageRecentMedia(photo) && (
                                                  <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-2"
                                                    onClick={() => handleEditDailyMedia(record.id, photo)}
                                                    title="Editar mídia"
                                                  >
                                                    <Pencil className="h-4 w-4" />
                                                  </Button>
                                                )}
                                                {canManageRecentMedia(photo) && (
                                                  <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-2 text-red-600"
                                                    onClick={() => handleDeleteDailyMedia(record.id, photo)}
                                                    title="Excluir mídia"
                                                  >
                                                    <Trash2 className="h-4 w-4" />
                                                  </Button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {canAttachDailyHistory && (
                                        <div className="pt-2">
                                          <Label className="text-xs">Adicionar foto/vídeo</Label>
                                          <CameraFileInput
                                            accept="image/*,video/*"
                                            onFileSelected={(file) => handleDailyMediaUpload(record.id, file)}
                                            fileLabel="Selecionar foto/vídeo"
                                            cameraLabel="Tirar foto"
                                          />
                                        </div>
                                      )}
                                      {getDailyRecordAuditItems(record).length > 0 && (
                                        <div className="mt-3 rounded border border-slate-200 bg-white p-3">
                                          <div className="mb-2 flex items-center gap-2 font-medium text-slate-800">
                                            <History className="h-4 w-4" />
                                            Últimas atualizações
                                          </div>
                                          <div className="space-y-2">
                                            {getDailyRecordAuditItems(record).map((item) => (
                                              <div key={item.id} className="rounded bg-slate-50 p-2 text-xs">
                                                <p className="font-medium text-slate-700">{item.fieldLabel}</p>
                                                <p className="text-slate-600">
                                                  {item.updatedAt ? new Date(item.updatedAt).toLocaleString('pt-BR') : 'N/A'} • {item.userName}
                                                </p>
                                                <p className="text-slate-600">
                                                  Antes: {formatAuditValue(item.before)} | Depois: {formatAuditValue(item.after)}
                                                </p>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                    {hasMoreDailyRecords && (
                      <div className="flex justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLoadOlderDailyRecords}
                          disabled={loadingMoreDailyRecords}
                        >
                          {loadingMoreDailyRecords ? 'Carregando...' : 'Carregar históricos antigos'}
                        </Button>
                      </div>
                    )}
                  </div>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="feeding" className="mt-6">
                <FeedingTab 
                  animalId={id} 
                  user={user}
                />
              </TabsContent>

              <TabsContent value="protocols" className="mt-6">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-emerald-800">Protocolos de Tratamento</h3>
                    {hasPermission(user, 'animals.edit_clinical') && (
                      <Button size="sm" onClick={() => (showMedForm ? closeMedicationProtocolForm() : openNewMedicationProtocolForm())} className="bg-emerald-600">
                        + Novo Protocolo
                      </Button>
                    )}
                  </div>

                  {showMedForm && (
                    <Card className="mb-4 bg-emerald-50">
                      <CardContent className="pt-6">
                        <form onSubmit={handleSubmitMedProtocol} className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-emerald-900">
                              {editingMedicationProtocol ? 'Editar protocolo de tratamento' : 'Novo protocolo de tratamento'}
                            </h4>
                            {editingMedicationProtocol && (
                              <p className="text-sm text-emerald-800">A correção ficará registrada na auditoria do prontuário.</p>
                            )}
                            {animalHasOutcome && !editingMedicationProtocol && (
                              <p className="text-sm text-amber-800">
                                Animal com desfecho registrado: ao salvar, será solicitada a palavra-chave {OUTCOME_PROTOCOL_CONFIRMATION_KEYWORD}.
                              </p>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <Label>Tipo do protocolo *</Label>
                              <Select value={treatmentProtocolType} onValueChange={setTreatmentProtocolType}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="medication">Medicamento</SelectItem>
                                  <SelectItem value="therapy">Terapia não medicamentosa</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {treatmentProtocolType === 'therapy' && (
                              <>
                                <div>
                                  <Label>Tipo de terapia *</Label>
                                  <Select value={therapyType} onValueChange={setTherapyType}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {THERAPY_TYPE_OPTIONS.map((option) => (
                                        <SelectItem key={option} value={option}>{option}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Duração da sessão</Label>
                                  <Input
                                    value={therapyDuration}
                                    onChange={(e) => setTherapyDuration(e.target.value)}
                                    placeholder="Ex: 15 minutos"
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <Label>Descrição</Label>
                                  <Textarea
                                    value={medicationName}
                                    onChange={(e) => setMedicationName(e.target.value)}
                                    placeholder="Conduta, região tratada ou observações da terapia"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Opcional.</p>
                                </div>
                              </>
                            )}
                            {treatmentProtocolType === 'medication' && (
                              <>
                            <div className="sm:col-span-2">
                              <Label>Princípio ativo *</Label>
                              <Input
                                list="medication-catalog-options"
                                value={medicationName}
                                onChange={(e) => handleMedicationNameChange(e.target.value)}
                                required
                                placeholder="Busque ou cadastre um princípio ativo"
                              />
                              <datalist id="medication-catalog-options">
                                {medicationCatalog.map((item) => (
                                  <option key={item.id} value={item.active_ingredient} />
                                ))}
                              </datalist>
                              {medicationName && !medicationNameExistsInCatalog && (
                                <p className="mt-1 text-xs text-amber-700">Novo princípio ativo: será cadastrado no catálogo ao salvar.</p>
                              )}
                              {medicationSuggestions.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {medicationSuggestions.map((item) => (
                                    <Button
                                      key={item.id}
                                      type="button"
                                      variant={item.id === selectedMedicationCatalogId ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => applyMedicationCatalogItem(item)}
                                      className={item.id === selectedMedicationCatalogId ? 'bg-emerald-600' : ''}
                                    >
                                      {item.active_ingredient}
                                    </Button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <Label>Apresentação</Label>
                              <Select value={creatingPresentation ? CREATE_NEW_VALUE : presentation} onValueChange={handlePresentationChange}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {presentationOptions.map((item) => (
                                    <SelectItem key={item} value={item}>{item}</SelectItem>
                                  ))}
                                  <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar nova apresentação</SelectItem>
                                </SelectContent>
                              </Select>
                              {creatingPresentation && (
                                <Input
                                  className="mt-2"
                                  value={presentation}
                                  onChange={(e) => setPresentation(e.target.value)}
                                  placeholder="Ex: comprimido 50 mg, solução 10 mg/mL"
                                />
                              )}
                            </div>
                            <div>
                              <Label>Dose *</Label>
                              <Input
                                list="medication-dose-options"
                                value={dosage}
                                onChange={(e) => setDosage(e.target.value)}
                                required
                                placeholder="Ex: 5 mg/kg"
                              />
                              <datalist id="medication-dose-options">
                                {dosageOptions.map((item) => (
                                  <option key={item} value={item} />
                                ))}
                              </datalist>
                              <p className="text-xs text-gray-500 mt-1">A dose sugerida pode ser editada conforme o animal.</p>
                            </div>
                            <div>
                              <Label>Via *</Label>
                              <Select value={creatingRoute ? CREATE_NEW_VALUE : route} onValueChange={handleRouteChange} required>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {routeOptions.map((item) => (
                                    <SelectItem key={item} value={item}>
                                      {item === 'IV' ? 'IV (Intravenosa)' : item === 'IM' ? 'IM (Intramuscular)' : item === 'SC' ? 'SC (Subcutanea)' : item}
                                    </SelectItem>
                                  ))}
                                  <SelectItem value={CREATE_NEW_VALUE}>+ Cadastrar nova via</SelectItem>
                                </SelectContent>
                              </Select>
                              {creatingRoute && (
                                <Input
                                  className="mt-2"
                                  value={route}
                                  onChange={(e) => setRoute(e.target.value)}
                                  placeholder="Digite a nova via"
                                />
                              )}
                            </div>
                              </>
                            )}
                            <div>
                              <Label>Frequencia *</Label>
                              <Select value={frequencyMode} onValueChange={setFrequencyMode}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {FREQUENCY_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {frequencyMode === 'specific_times' ? (
                                <Input
                                  className="mt-2"
                                  value={specificTimesInput}
                                  onChange={(e) => setSpecificTimesInput(e.target.value)}
                                  placeholder="Ex: 08:00, 14:00, 20:00"
                                />
                              ) : frequencyMode === 'times_per_day' ? (
                                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                  {normalizeTimeOptions(normalizeTimesPerDayInput(specificTimesInput, frequencyValue)).map((timeValue, index, times) => (
                                    <div key={`dose-time-${index}`}>
                                      <Label className="text-xs">Horário {index + 1}</Label>
                                      <Input
                                        type="time"
                                        value={timeValue}
                                        onChange={(event) => {
                                          const nextTimes = [...times];
                                          nextTimes[index] = event.target.value;
                                          setSpecificTimesInput(nextTimes.join(', '));
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : frequencyMode !== 'sos' ? (
                                <Input
                                  className="mt-2"
                                  type="number"
                                  min="1"
                                  value={frequencyValue}
                                  onChange={(e) => setFrequencyValue(e.target.value)}
                                  placeholder={frequencyMode === 'interval_hours' ? 'Ex: 8' : 'Ex: 3'}
                                />
                              ) : null}
                              <Input
                                className="mt-2"
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value)}
                                placeholder="Descrição livre opcional"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Visualização: {buildProtocolFrequencyLabel(
                                  frequencyMode,
                                  Number(frequencyValue),
                                  specificTimesInput.split(',').map((item) => item.trim()).filter(Boolean),
                                  frequency
                                )}
                              </p>
                            </div>
                            <div>
                              <Label>Data de Início *</Label>
                              <Input type="date" value={protocolStartDate} onChange={(e) => setProtocolStartDate(e.target.value)} required />
                            </div>
                            <div>
                              <Label>Data Final</Label>
                              <Input type="date" value={protocolEndDate} onChange={(e) => setProtocolEndDate(e.target.value)} />
                            </div>
                            <div>
                              <Label>Horário de Início *</Label>
                              <Input 
                                type="time" 
                                value={startTime} 
                                onChange={(e) => setStartTime(e.target.value)} 
                                required 
                                data-testid="start-time-input"
                              />
                              <p className="text-xs text-gray-500 mt-1">Horário da primeira dose do dia</p>
                            </div>
                            <div>
                              <Label>Duração calculada</Label>
                              <Input 
                                value={protocolStartDate && protocolEndDate
                                  ? `${Math.max(1, Math.floor((new Date(`${protocolEndDate}T00:00:00`) - new Date(`${protocolStartDate}T00:00:00`)) / 86400000) + 1)} dia(s)`
                                  : ''
                                }
                                readOnly
                                placeholder="Indefinida"
                                data-testid="duration-days-input"
                              />
                              <p className="text-xs text-gray-500 mt-1">Calculada automaticamente pelas datas de início e término.</p>
                            </div>
                            {editingMedicationProtocol && (
                              <div className="sm:col-span-2">
                                <Label>Motivo da correção *</Label>
                                <Textarea
                                  value={protocolEditReason}
                                  onChange={(e) => setProtocolEditReason(e.target.value)}
                                  placeholder="Descreva por que o protocolo está sendo corrigido"
                                  required
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button type="submit" className="bg-emerald-600" disabled={savingMedicationProtocol}>
                              {savingMedicationProtocol ? 'Salvando...' : (editingMedicationProtocol ? 'Salvar correção' : 'Salvar')}
                            </Button>
                            <Button type="button" variant="outline" onClick={closeMedicationProtocolForm} disabled={savingMedicationProtocol}>Cancelar</Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  )}

                  {medProtocols.length === 0 ? (
                    <p className="text-gray-500 py-4">Nenhum protocolo</p>
                  ) : (
                    <div className="space-y-3">
                      {medProtocols.map((p) => {
                        const lastExecution = getLastExecution(p.id);
                        const executions = protocolExecutions[p.id] || [];
                        const protocolInactive = p.active === false;
                        const isTherapyProtocol = p.protocol_type === 'therapy';
                        const protocolStatus = p.status === 'cancelled' ? 'Cancelado' : p.status === 'suspended' ? 'Suspenso' : 'Ativo';
                        const canPermanentlyDeleteProtocol = canPermanentlyDeleteProtocols && ['cancelled', 'suspended'].includes(p.status);
                        
                        return (
                          <Card key={p.id} className={`border-l-4 ${protocolInactive ? 'border-gray-400 bg-gray-50' : 'border-emerald-500'}`}>
                            <CardContent className="pt-4">
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="font-semibold text-base md:text-lg">{isTherapyProtocol ? (p.therapy_type || p.medication_name) : p.medication_name}</p>
                                    <Badge className={isTherapyProtocol ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}>
                                      {isTherapyProtocol ? 'Terapia' : 'Medicamento'}
                                    </Badge>
                                    <Badge className={protocolInactive ? 'bg-gray-200 text-gray-700' : 'bg-emerald-100 text-emerald-800'}>
                                      {protocolStatus}
                                    </Badge>
                                  </div>
                                  {!isTherapyProtocol && p.presentation && <p className="text-sm text-gray-600">{p.presentation}</p>}
                                  {isTherapyProtocol ? (
                                    <>
                                      <p className="text-sm text-gray-600">
                                        {p.frequency}{p.therapy_duration ? ` - ${p.therapy_duration}` : ''}
                                      </p>
                                      {p.notes && <p className="text-sm text-gray-600">{p.notes}</p>}
                                    </>
                                  ) : (
                                    <p className="text-sm text-gray-600">{p.dosage} - {p.route} - {p.frequency}</p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    Vigência: {new Date(p.start_date).toLocaleDateString('pt-BR')}
                                    {p.end_date ? ` até ${new Date(p.end_date).toLocaleDateString('pt-BR')}` : ' sem data final'}
                                  </p>
                                  {p.updated_by_name && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      Última alteração por {p.updated_by_name}
                                      {p.updated_at ? ` em ${new Date(p.updated_at).toLocaleString('pt-BR')}` : ''}
                                    </p>
                                  )}
                                  {p.suspension_reason && <p className="mt-1 text-xs text-gray-600"><strong>Motivo da suspensão:</strong> {p.suspension_reason}</p>}
                                  {p.cancellation_reason && <p className="mt-1 text-xs text-gray-600"><strong>Motivo do cancelamento:</strong> {p.cancellation_reason}</p>}
                                </div>
                                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                  {p.status !== 'cancelled' && canManageTreatmentProtocols && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                      onClick={() => openEditMedicationProtocolForm(p)}
                                    >
                                      <Pencil className="w-4 h-4 mr-1" />
                                      Editar
                                    </Button>
                                  )}
                                  <Button 
                                    onClick={() => openExecutionDialog(p)} 
                                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto min-h-[44px] font-semibold"
                                  >
                                    ✓ Executar
                                  </Button>
                                  {!protocolInactive && canManageTreatmentProtocols && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                      onClick={() => handleSuspendMedicationProtocol(p)}
                                    >
                                      Suspender
                                    </Button>
                                  )}
                                  {canManageTreatmentProtocols && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="text-red-600 border-red-300 hover:bg-red-50"
                                      onClick={() => handleDeleteMedicationProtocol(p)}
                                      disabled={p.status === 'cancelled'}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Cancelar
                                    </Button>
                                  )}
                                  {canPermanentlyDeleteProtocol && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="text-red-700 border-red-500 hover:bg-red-50"
                                      onClick={() => handlePermanentDeleteMedicationProtocol(p)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Excluir permanente
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              {executions.length > 0 ? (
                                <div className="mt-3 pt-3 border-t">
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:space-x-0">
                                    <Badge className="bg-green-100 text-green-800 border-green-300">
                                      Executado {executions.length}x
                                    </Badge>
                                    {lastExecution && (
                                      <p className="text-xs text-gray-600">
                                        Ultima: {new Date(lastExecution.execution_date).toLocaleString('pt-BR')}
                                      </p>
                                    )}
                                  </div>
                                  <details className="mt-2">
                                    <summary className="text-sm text-emerald-700 cursor-pointer hover:underline">
                                      Ver historico completo ({executions.length})
                                    </summary>
                                    <div className="mt-2 space-y-1 pl-4">
                                      {executions.slice(0, 10).map((exec, idx) => (
                                        <div key={idx} className="flex flex-col gap-1 rounded border bg-gray-50 p-2 text-xs text-gray-600">
                                          <p>
                                            • {new Date(exec.execution_date).toLocaleString('pt-BR')} - {exec.status}
                                            {exec.scheduled_hour !== undefined && exec.scheduled_hour !== null ? ` • horário programado ${formatHour(exec.scheduled_hour)}` : ''}
                                          </p>
                                          {exec.notes && <p><strong>Obs:</strong> {exec.notes}</p>}
                                          {exec.executed_by_name && <p><strong>Registrado por:</strong> {exec.executed_by_name}</p>}
                                          {exec.date_time_justification && <p><strong>Justificativa data/hora:</strong> {exec.date_time_justification}</p>}
                                          {exec.updated_by_name && <p><strong>Alterado por:</strong> {exec.updated_by_name}{exec.updated_at ? ` em ${new Date(exec.updated_at).toLocaleString('pt-BR')}` : ''}</p>}
                                          {exec.update_reason && <p><strong>Motivo da alteração:</strong> {exec.update_reason}</p>}
                                          {exec.cancellation_reason && <p><strong>Motivo do cancelamento:</strong> {exec.cancellation_reason}</p>}
                                          <div className="flex flex-wrap items-center gap-2 pt-1">
                                            <CameraFileInput
                                              accept="image/*,video/*"
                                              disabled={uploadingExecutionId === exec.id}
                                              onFileSelected={(file) => handleExecutionMediaUpload(exec.id, file)}
                                              fileLabel={uploadingExecutionId === exec.id ? 'Enviando...' : 'Anexar foto/vídeo'}
                                              cameraLabel="Tirar foto"
                                            />
                                            {exec.media?.length > 0 && (
                                              <span className="text-gray-500">{exec.media.length} mídia(s)</span>
                                            )}
                                          </div>
                                          {exec.media?.length > 0 && (
                                            <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                                              {exec.media.map((mediaItem, mediaIndex) => (
                                                <div
                                                  key={`${exec.id}-media-${mediaIndex}`}
                                                  className="flex items-center justify-between gap-2 rounded border bg-white p-2 text-xs"
                                                >
                                                  <a
                                                    href={`${API}${mediaItem.file_url}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="flex min-w-0 flex-1 items-center gap-2 text-blue-700 underline"
                                                    onClick={(event) => handleOpenMediaFile(event, mediaItem)}
                                                  >
                                                    <ProtectedMediaThumbnail
                                                      media={mediaItem}
                                                      alt={mediaItem.caption || mediaItem.filename || `Mídia ${mediaIndex + 1}`}
                                                      className="h-12 w-16"
                                                      fallbackClassName="h-12 w-16"
                                                    />
                                                    <span className="truncate">{mediaItem.caption || mediaItem.filename || `Mídia ${mediaIndex + 1}`}</span>
                                                  </a>
                                                  {canDeleteAttachedMedia && (
                                                    <Button
                                                      type="button"
                                                      variant="outline"
                                                      size="sm"
                                                      className="h-7 px-2 text-red-600"
                                                      onClick={() => handleDeleteExecutionMedia(exec.id, mediaItem)}
                                                      title="Excluir mídia"
                                                    >
                                                      <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {canManageTreatmentProtocols && (
                                            <div className="flex gap-2">
                                              <Button type="button" variant="outline" size="sm" onClick={() => openExecutionDialog(p, exec)}>
                                                Alterar
                                              </Button>
                                              <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteExecution(exec.id)} disabled={exec.status === 'cancelado'}>
                                                Cancelar
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>
                              ) : (
                                <div className="mt-3 pt-3 border-t">
                                  <Badge variant="outline" className="text-gray-500">Ainda nao executado</Badge>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>


              </TabsContent>

              {/* Aba 4: Exames Complementares */}
              <TabsContent value="exams" className="mt-6">
                <ExamsTab animalId={id} user={user} initialExamId={searchParams.get('exam_id') || ''} />
              </TabsContent>

              <TabsContent value="outcome" className="mt-6">
                <OutcomeTab 
                  animalId={id} 
                  animal={animal} 
                  user={user}
                  onOutcomeChange={fetchAnimalData}
                />
              </TabsContent>

              <TabsContent value="necropsy" className="mt-6">
                <NecropsyTab
                  animalId={id}
                  animal={animal}
                  user={user}
                  onNecropsyChange={refreshAnimalSummary}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      <Dialog open={Boolean(editingDailyRecord)} onOpenChange={(open) => !open && setEditingDailyRecord(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar histórico de acompanhamento</DialogTitle>
            <DialogDescription>
              A alteração será registrada no histórico do acompanhamento e na auditoria do sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Data do acompanhamento *</Label>
                <Input type="date" value={dailyRecordForm.record_date} onChange={(e) => updateDailyRecordForm('record_date', e.target.value)} />
              </div>
              <div>
                <Label>Horário do acompanhamento *</Label>
                <Input type="time" value={dailyRecordForm.record_time} onChange={(e) => updateDailyRecordForm('record_time', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label>Temperatura (C)</Label>
                <Input type="number" step="0.1" value={dailyRecordForm.temperature} onChange={(e) => updateDailyRecordForm('temperature', e.target.value)} />
              </div>
              <div>
                <Label>Glicose (mg/dL)</Label>
                <Input type="number" step="0.1" value={dailyRecordForm.glucose} onChange={(e) => updateDailyRecordForm('glucose', e.target.value)} />
              </div>
              <div>
                <Label>Peso (kg)</Label>
                <Input type="number" step="0.01" value={dailyRecordForm.weight} onChange={(e) => updateDailyRecordForm('weight', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label>Apetite</Label>
                <Input value={dailyRecordForm.appetite} onChange={(e) => updateDailyRecordForm('appetite', e.target.value)} />
              </div>
              <div>
                <Label>Comportamento</Label>
                <Input value={dailyRecordForm.behavior} onChange={(e) => updateDailyRecordForm('behavior', e.target.value)} />
              </div>
              <div>
                <Label>Hidratação</Label>
                <Input value={dailyRecordForm.hydration} onChange={(e) => updateDailyRecordForm('hydration', e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Evolução Clínica *</Label>
              <Textarea value={dailyRecordForm.clinical_evolution} onChange={(e) => updateDailyRecordForm('clinical_evolution', e.target.value)} rows={4} />
            </div>
            <div>
              <Label>Prognóstico *</Label>
              <Input value={dailyRecordForm.prognosis} onChange={(e) => updateDailyRecordForm('prognosis', e.target.value)} />
            </div>
            <div>
              <Label>Exames solicitados</Label>
              <Input value={dailyRecordForm.requested_exam_types} onChange={(e) => updateDailyRecordForm('requested_exam_types', e.target.value)} placeholder="Separe por vírgula" />
            </div>
            <div>
              <Label>Observação para laboratório</Label>
              <Textarea value={dailyRecordForm.lab_alert_notes} onChange={(e) => updateDailyRecordForm('lab_alert_notes', e.target.value)} rows={2} />
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setEditingDailyRecord(null)} disabled={savingDailyRecordEdit}>
                Cancelar
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveDailyRecordEdit} disabled={savingDailyRecordEdit}>
                {savingDailyRecordEdit ? 'Salvando...' : 'Salvar alteração'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showExecutionDialog}
        onOpenChange={(open) => {
          setShowExecutionDialog(open);
          if (!open) setExecutionMediaFile(null);
        }}
      >
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>{editingExecution ? 'Alterar Execução' : 'Executar Protocolo'}</DialogTitle>
            <DialogDescription>
              {executionProtocol?.medication_name || 'Selecione o horário programado para registrar a medicação.'}
            </DialogDescription>
          </DialogHeader>
          {executionProtocol && (
            <div className="space-y-4">
              {parseProtocolHours(executionProtocol).length > 0 ? (
                <div>
                  <Label>Horário programado</Label>
                  <Select value={executionScheduledHour} onValueChange={setExecutionScheduledHour}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {parseProtocolHours(executionProtocol).map((hour) => (
                        <SelectItem key={hour} value={String(hour)}>{formatHour(hour)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Este protocolo está em modo SOS e será registrado sem horário fixo.</p>
              )}
              {canSetProtocolExecutionDateTime(user) && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Data executada</Label>
                    <Input
                      type="date"
                      value={executionDate}
                      onChange={(e) => setExecutionDate(e.target.value)}
                      data-testid="protocol-execution-date-input"
                    />
                  </div>
                  <div>
                    <Label>Hora executada</Label>
                    <Input
                      type="time"
                      value={executionTime}
                      onChange={(e) => setExecutionTime(e.target.value)}
                      data-testid="protocol-execution-time-input"
                    />
                  </div>
                </div>
              )}
              {canSetProtocolExecutionDateTime(user) && (
                <div>
                  <Label>Justificativa para data/hora retroativa ou correção</Label>
                  <Textarea
                    value={executionDateTimeJustification}
                    onChange={(e) => setExecutionDateTimeJustification(e.target.value)}
                    rows={2}
                    placeholder="Obrigatória quando a data/hora for diferente do horário padrão da execução"
                  />
                </div>
              )}
              <div>
                <Label>Status</Label>
                <Select value={executionStatus} onValueChange={setExecutionStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realizado">Realizado</SelectItem>
                    <SelectItem value="nao_realizado">Não realizado</SelectItem>
                    <SelectItem value="atrasado">Atrasado</SelectItem>
                    <SelectItem value="suspenso">Suspenso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observações</Label>
                <Textarea value={executionNotes} onChange={(e) => setExecutionNotes(e.target.value)} rows={3} />
              </div>
              {!editingExecution && (
                <div>
                  <Label>Foto da execução (opcional)</Label>
                  <CameraFileInput
                    accept="image/*"
                    disabled={savingExecution}
                    onFileSelected={setExecutionMediaFile}
                    fileLabel="Selecionar foto"
                    cameraLabel="Tirar foto"
                    data-testid="protocol-execution-photo-input"
                  />
                  {executionMediaFile && (
                    <p className="mt-1 text-xs text-gray-500">Selecionado: {executionMediaFile.name}</p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowExecutionDialog(false);
                    setExecutionMediaFile(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveExecution} disabled={savingExecution}>
                  {savingExecution ? 'Salvando...' : (editingExecution ? 'Salvar alteração' : 'Executar')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
